/**
 * Chaves de API (Anthropic) — tela do DONO. Cadastro de todas as chaves com o
 * nome de quem pertence + a chave (mascarada, com revelar/copiar). O Dono depois
 * atribui uma chave a cada condomínio que tenha a sua (no cadastro do condomínio).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Copy, Eye, EyeOff, KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../lib/api";

interface Chave { id: number; nome: string; mascarada: string; usada_por: string[] }

export function ChavesApiPage({ onVoltar }: { onVoltar: () => void }) {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: ["chaves-api"] });
  const { data, isLoading } = useQuery<{ chaves: Chave[] }>({
    queryKey: ["chaves-api"],
    queryFn: () => api.get("/chaves-api"),
  });

  const [nome, setNome] = useState(""); const [chave, setChave] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const criar = useMutation({
    mutationFn: () => api.post("/chaves-api", { nome, chave }),
    onSuccess: () => { setNome(""); setChave(""); setErro(null); inval(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar."),
  });

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={16} /> Voltar
      </button>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-10 h-10 rounded-xl bg-brand-700 text-white flex items-center justify-center"><KeyRound size={20} /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Chaves de API</h1>
          <p className="text-sm text-slate-500">Cadastre as chaves (Anthropic) e depois atribua uma a cada condomínio que tenha a sua.</p>
        </div>
      </div>

      {/* Nova chave */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mt-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5"><Plus size={16} /> Cadastrar chave</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.5fr_auto] gap-2">
          <input placeholder="De quem é (ex.: Bourbon)" value={nome} onChange={(e) => setNome(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500" />
          <input placeholder="Chave (sk-ant-...)" value={chave} onChange={(e) => setChave(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono outline-none focus:border-brand-500" />
          <button disabled={!chave || criar.isPending} onClick={() => criar.mutate()}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-700 text-white px-3 py-2 text-sm font-medium hover:bg-brand-800 disabled:opacity-50">
            {criar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Salvar
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">A chave é guardada <b>cifrada</b>. Pegue em console.anthropic.com → API Keys.</p>
        {erro && <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{erro}</div>}
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="text-sm text-slate-400 flex items-center gap-2 mt-4"><Loader2 size={16} className="animate-spin" /> Carregando…</div>
      ) : (
        <div className="space-y-2 mt-5">
          {(data?.chaves ?? []).length === 0 && <p className="text-sm text-slate-500">Nenhuma chave cadastrada ainda.</p>}
          {(data?.chaves ?? []).map((k) => <ChaveCard key={k.id} k={k} inval={inval} />)}
        </div>
      )}
    </div>
  );
}

function ChaveCard({ k, inval }: { k: Chave; inval: () => void }) {
  const [revelada, setRevelada] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const revelar = useMutation({
    mutationFn: () => api.get<{ chave: string }>(`/chaves-api/${k.id}/revelar`),
    onSuccess: (r) => setRevelada(r.chave),
  });
  const remover = useMutation({
    mutationFn: () => api.delete(`/chaves-api/${k.id}`),
    onSuccess: inval,
  });
  const copiar = async () => {
    const val = revelada ?? (await api.get<{ chave: string }>(`/chaves-api/${k.id}/revelar`)).chave;
    setRevelada(val);
    try { await navigator.clipboard.writeText(val); setCopiado(true); setTimeout(() => setCopiado(false), 1500); } catch { /* */ }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900">{k.nome}</div>
          <div className="text-xs text-slate-500 font-mono break-all">{revelada ?? k.mascarada}</div>
          {k.usada_por.length > 0 ? (
            <div className="text-[11px] text-emerald-700 mt-1">Usada por: {k.usada_por.join(", ")}</div>
          ) : (
            <div className="text-[11px] text-slate-400 mt-1">Não está atribuída a nenhum condomínio — remover é seguro.</div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => (revelada ? setRevelada(null) : revelar.mutate())} title={revelada ? "Ocultar" : "Revelar"}
            className="p-1.5 text-slate-500 hover:text-brand-700">{revelada ? <EyeOff size={16} /> : <Eye size={16} />}</button>
          <button onClick={copiar} title="Copiar" className="p-1.5 text-slate-500 hover:text-brand-700">
            {copiado ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
          </button>
          <button onClick={() => {
            const aviso = k.usada_por.length
              ? `\n\n⚠️ ATENÇÃO: esta chave está EM USO por: ${k.usada_por.join(", ")}.\nAo remover, esse(s) condomínio(s) passam a usar a chave padrão do Dono.`
              : `\n\nNão está atribuída a nenhum condomínio — remover é seguro (não afeta nenhum outro).`;
            if (confirm(`Remover a chave "${k.nome}"?${aviso}`)) remover.mutate();
          }}
            title="Remover" className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 size={16} /></button>
        </div>
      </div>
    </div>
  );
}
